// src/pages/IDCardGenerator.jsx
// Employee ID Card Generator — 4K HD downloadable PNG
// Access: superadmin always, admin only if idCardAccess === true in Firestore

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import useIdCardAccess from '../hooks/useIdCardAccess';
import html2canvas from 'html2canvas-pro';
import { jsPDF } from 'jspdf';
import toast from 'react-hot-toast';
import {
  FaDownload, FaIdCard, FaUser, FaSync,
  FaSearch, FaTimes, FaCamera, FaLock, FaFilePdf,
} from 'react-icons/fa';
import styles from './IDCardGenerator.module.css';

const EMPTY_CARD = {
  name: '',
  employeeId: '',
  joiningDate: '',
  principle1: 'Trust your capabilities; never doubt yourself. (Mindset)',
  principle2: 'Work on weaknesses; never just compete. (Growth)',
  principle3: 'Focus on learning; don\'t rush for completion. (Process)',
  principle4: 'Maximize today; don\'t leave it for tomorrow. (Action)',
  principle5: 'Work hard today; make tomorrow flourished. (Outcome)',
};

const PRINCIPLE_COLORS = [
  '#e85d04', // 1 — orange
  '#1d4ed8', // 2 — blue
  '#16a34a', // 3 — green
  '#9333ea', // 4 — purple
  '#dc2626', // 5 — red
];

const IDCardGenerator = () => {
  const { userData } = useAuth();
  const { hasAccess, loading: accessLoading } = useIdCardAccess();
  const cardRef = useRef(null);
  const backCardRef = useRef(null);
  const fileInputRef = useRef(null);

  const [employees, setEmployees] = useState([]);
  const [cardData, setCardData] = useState(EMPTY_CARD);
  const [photo, setPhoto] = useState(null);           // data URL or storage URL
  const [empSearch, setEmpSearch] = useState('');
  const [showEmpDropdown, setShowEmpDropdown] = useState(false);
  const [selectedEmpId, setSelectedEmpId] = useState('');

  // Load users list for the employee selector
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'users'),
      snap => setEmployees(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      err => console.error('Failed to load users:', err)
    );
    return unsub;
  }, []);

  const filteredEmployees = employees.filter(e =>
    `${e.name || ''} ${e.email || ''} ${e.employeeId || ''}`
      .toLowerCase()
      .includes(empSearch.toLowerCase())
  );

  const handleSelectEmployee = useCallback((emp) => {
    setSelectedEmpId(emp.id);
    setEmpSearch(emp.name || emp.email || '');
    setShowEmpDropdown(false);
    setCardData(prev => ({
      ...prev,                          // preserve principles
      name: emp.name || '',
      employeeId: emp.employeeId || '',
      joiningDate: emp.joiningDate ? String(emp.joiningDate).substring(0, 10) : '',
    }));
    if (emp.photoURL) setPhoto(emp.photoURL);
  }, []);

  const handleChange = (field, value) => {
    setCardData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file.');
      return;
    }
    const reader = new FileReader();
    reader.onload = ev => setPhoto(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleReset = () => {
    setCardData(EMPTY_CARD);
    setPhoto(null);
    setSelectedEmpId('');
    setEmpSearch('');
  };

  const [isDownloadingFront, setIsDownloadingFront] = useState(false);
  const [isDownloadingBack, setIsDownloadingBack] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  const softCopyRef = useRef(null);

  const downloadCard = async (ref, suffix, setLoading) => {
    if (!ref.current) return;
    setLoading(true);
    try {
      const canvas = await html2canvas(ref.current, {
        scale: 4,
        useCORS: true,
        allowTaint: false,
        backgroundColor: null,
        logging: false,
        imageTimeout: 20000,
        onclone: (_doc, clonedEl) => {
          // html2canvas ignores object-fit:cover — manually simulate it
          const imgEl = clonedEl.querySelector('[data-download-fix="photo"]');
          if (!imgEl) return;
          const slot = imgEl.parentElement;
          const sw = slot.offsetWidth;
          const sh = slot.offsetHeight;
          const iw = imgEl.naturalWidth || imgEl.width;
          const ih = imgEl.naturalHeight || imgEl.height;
          if (!iw || !ih || !sw || !sh) return;
          const scale = Math.max(sw / iw, sh / ih);
          const rw = Math.round(iw * scale);
          const rh = Math.round(ih * scale);
          imgEl.style.width = `${rw}px`;
          imgEl.style.height = `${rh}px`;
          imgEl.style.objectFit = 'fill';
          imgEl.style.marginLeft = `${Math.round((sw - rw) / 2)}px`;
          imgEl.style.marginTop = `${Math.round((sh - rh) / 2)}px`;
        },
      });
      const baseName = (cardData.name || 'ID_Card').replace(/\s+/g, '_');
      const link = document.createElement('a');
      link.download = `${baseName}_PrepX_ID_Card_${suffix}.png`;
      link.href = canvas.toDataURL('image/png', 1.0);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`${suffix} side downloaded!`);
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Download failed — please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Soft Copy PDF — both sides on one professional A4 page ───────────────
  const downloadSoftCopyPdf = async () => {
    if (!cardRef.current || !backCardRef.current) return;
    setIsDownloadingPdf(true);
    const photoFixClone = (_doc, clonedEl) => {
      const imgEl = clonedEl.querySelector('[data-download-fix="photo"]');
      if (!imgEl) return;
      const slot = imgEl.parentElement;
      const sw = slot.offsetWidth; const sh = slot.offsetHeight;
      const iw = imgEl.naturalWidth || imgEl.width;
      const ih = imgEl.naturalHeight || imgEl.height;
      if (!iw || !ih || !sw || !sh) return;
      const sc = Math.max(sw / iw, sh / ih);
      const rw = Math.round(iw * sc); const rh = Math.round(ih * sc);
      imgEl.style.width = `${rw}px`; imgEl.style.height = `${rh}px`;
      imgEl.style.objectFit = 'fill';
      imgEl.style.marginLeft = `${Math.round((sw - rw) / 2)}px`;
      imgEl.style.marginTop  = `${Math.round((sh - rh) / 2)}px`;
    };
    try {
      const [frontCanvas, backCanvas] = await Promise.all([
        html2canvas(cardRef.current,     { scale: 3, useCORS: true, allowTaint: false, backgroundColor: null, logging: false, imageTimeout: 20000, onclone: photoFixClone }),
        html2canvas(backCardRef.current, { scale: 3, useCORS: true, allowTaint: false, backgroundColor: null, logging: false, imageTimeout: 20000 }),
      ]);

      // ── A4 portrait: 210 × 297 mm ──────────────────────────────────────
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const PW = 210; const PH = 297;

      // Page background — light sky blue so white ID cards stand out
      pdf.setFillColor(224, 242, 254);          // #e0f2fe sky-100
      pdf.rect(0, 0, PW, PH, 'F');

      // ══════════════════════════════════════════════════════════════════
      // HEADER
      // ══════════════════════════════════════════════════════════════════

      // Outer deep-blue band
      pdf.setFillColor(15, 23, 74);             // very dark navy #0f1749
      pdf.rect(0, 0, PW, 32, 'F');

      // Inner accent strip at very top (1.5 mm orange)
      pdf.setFillColor(249, 115, 22);           // #f97316 orange
      pdf.rect(0, 0, PW, 1.5, 'F');

      // Logo — left side, vertically centred in banner
      try {
        const logoImg = new Image();
        logoImg.crossOrigin = 'anonymous';
        await new Promise((res) => { logoImg.onload = res; logoImg.onerror = res; logoImg.src = '/logo.jpg'; });
        if (logoImg.naturalWidth) {
          const lc = document.createElement('canvas');
          lc.width = logoImg.naturalWidth; lc.height = logoImg.naturalHeight;
          lc.getContext('2d').drawImage(logoImg, 0, 0);
          const ar = logoImg.naturalWidth / logoImg.naturalHeight;
          const lh = 16; const lw = lh * ar;
          pdf.addImage(lc.toDataURL('image/png'), 'PNG', 9, 9, lw, lh);
        }
      } catch (_) { /* logo optional */ }

      // Org name — centre
      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(17);
      pdf.text('PrrepX Pathshala', PW / 2, 14, { align: 'center' });

      // Blue tagline — centre
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7.5);
      pdf.setTextColor(147, 197, 253);          // #93c5fd light blue
      pdf.text('One Stop Solution for JEE/NEET & Class 6\u201312th', PW / 2, 20.5, { align: 'center' });

      // Decorative dot-separator
      pdf.setFillColor(249, 115, 22);
      for (let i = 0; i < 3; i++) {
        pdf.circle(PW / 2 - 4 + i * 4, 25.5, 0.8, 'F');
      }

      // Faculty tagline — centre, smaller
      pdf.setFont('helvetica', 'italic');
      pdf.setFontSize(6.5);
      pdf.setTextColor(186, 230, 253);          // #bae6fd
      pdf.text('100% Faculties from IIT/NIT & top Medical Alumni', PW / 2, 30, { align: 'center' });

      // Bottom accent line of header
      pdf.setFillColor(249, 115, 22);
      pdf.rect(0, 32, PW, 0.8, 'F');

      // ══════════════════════════════════════════════════════════════════
      // DOCUMENT IDENTITY BLOCK (light grey area below header)
      // ══════════════════════════════════════════════════════════════════
      pdf.setFillColor(241, 245, 249);          // #f1f5f9
      pdf.rect(0, 32.8, PW, 14, 'F');

      // "EMPLOYEE IDENTITY CARD" label
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(15, 23, 74);
      pdf.text('EMPLOYEE IDENTITY CARD', PW / 2, 40, { align: 'center' });

      // Employee name + ID (if filled)
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7);
      pdf.setTextColor(71, 85, 105);            // #475569
      const idLine = [
        cardData.name ? cardData.name : null,
        cardData.employeeId ? `ID: ${cardData.employeeId}` : null,
        cardData.joiningDate ? `Joining: ${cardData.joiningDate}` : null,
      ].filter(Boolean).join('   \u2022   ');
      if (idLine) pdf.text(idLine, PW / 2, 44.5, { align: 'center' });

      // horizontal rule
      pdf.setDrawColor(203, 213, 225);
      pdf.setLineWidth(0.3);
      pdf.line(10, 46.8, PW - 10, 46.8);

      // ══════════════════════════════════════════════════════════════════
      // CARDS  (CR80 = 85.6 × 54 mm ratio)
      // Fit width to page with margins: usable width = 190 mm → card = 160 mm wide
      // ══════════════════════════════════════════════════════════════════
      const cardW = 160;
      const cardH = cardW * (54 / 85.6);       // ≈ 100.7 mm
      const cardX = (PW - cardW) / 2;          // centred horizontally

      const now = new Date();
      const dateStr = now.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: '2-digit', year: 'numeric' });

      // ── FRONT CARD ────────────────────────────────────────────────────
      const frontLabelY = 52;
      // Pill label background
      pdf.setFillColor(30, 58, 138);            // #1e3a8a
      pdf.roundedRect(cardX, frontLabelY, 38, 5.5, 1.5, 1.5, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(6.5);
      pdf.text('FRONT  SIDE', cardX + 19, frontLabelY + 3.8, { align: 'center' });

      const frontCardY = frontLabelY + 7;
      pdf.addImage(frontCanvas.toDataURL('image/png', 1.0), 'PNG', cardX, frontCardY, cardW, cardH);

      // ── BACK CARD ─────────────────────────────────────────────────────
      const backLabelY = frontCardY + cardH + 7;
      pdf.setFillColor(5, 150, 105);            // #059669 green
      pdf.roundedRect(cardX, backLabelY, 36, 5.5, 1.5, 1.5, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(6.5);
      pdf.text('BACK  SIDE', cardX + 18, backLabelY + 3.8, { align: 'center' });

      const backCardY = backLabelY + 7;
      pdf.addImage(backCanvas.toDataURL('image/png', 1.0), 'PNG', cardX, backCardY, cardW, cardH);

      // ══════════════════════════════════════════════════════════════════
      // FOOTER
      // ══════════════════════════════════════════════════════════════════
      const footerY = PH - 14;

      // Orange top accent
      pdf.setFillColor(249, 115, 22);
      pdf.rect(0, footerY, PW, 0.8, 'F');

      // Dark footer band
      pdf.setFillColor(15, 23, 74);
      pdf.rect(0, footerY + 0.8, PW, 13.2, 'F');

      // Left: website
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(6.5);
      pdf.setTextColor(147, 197, 253);
      pdf.text('https://prrepxpathshala.live', 10, footerY + 6);

      // Centre: CONFIDENTIAL watermark-style text
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(7);
      pdf.setTextColor(249, 115, 22);
      pdf.text('OFFICIAL DOCUMENT \u2014 FOR INTERNAL USE ONLY', PW / 2, footerY + 6, { align: 'center' });

      // Right: date
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(6.5);
      pdf.setTextColor(147, 197, 253);
      pdf.text(`Issued: ${dateStr}`, PW - 10, footerY + 6, { align: 'right' });

      // Second footer line
      pdf.setFontSize(6);
      pdf.setTextColor(100, 149, 200);
      pdf.text('\u00a9 ' + now.getFullYear() + ' PrrepX Pathshala. All rights reserved.', PW / 2, footerY + 11, { align: 'center' });

      // ── Save ──────────────────────────────────────────────────────────
      const baseName = (cardData.name || 'ID_Card').replace(/\s+/g, '_');
      pdf.save(`${baseName}_SoftCopy_ID.pdf`);
      toast.success('Soft Copy PDF downloaded!');
    } catch (err) {
      console.error('PDF error:', err);
      toast.error('PDF generation failed — please try again.');
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  // ── Access gate ─────────────────────────────────────────
  if (accessLoading) {
    return (
      <div className={styles.centeredState}>
        <div className={styles.spinner} />
        <p>Loading...</p>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className={styles.accessDenied}>
        <FaLock className={styles.accessDeniedIcon} />
        <h2>Access Restricted</h2>
        <p>You don't have permission to use the ID Card Generator.</p>
        <p>Contact your superadmin to request access.</p>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────
  return (
    <div className={styles.page}>

      {/* ─── Page header ─── */}
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <FaIdCard className={styles.pageHeaderIcon} />
          <div>
            <h1 className={styles.pageTitle}>ID Card Generator</h1>
            <p className={styles.pageSubtitle}>
              Generate & download 4K print-ready employee ID cards
            </p>
          </div>
        </div>
        <button className={styles.resetBtn} onClick={handleReset} title="Clear all fields">
          <FaSync /> Reset
        </button>
      </div>

      <div className={styles.layout}>

        {/* ════ LEFT: Form panel ════ */}
        <div className={styles.formPanel}>

          {/* Employee selector */}
          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Auto-fill from Employee</h3>
            <div
              className={styles.empSelectorWrapper}
              onBlur={() => setTimeout(() => setShowEmpDropdown(false), 180)}
            >
              <div className={styles.empSelectorInput}>
                <FaSearch className={styles.empSearchIcon} />
                <input
                  type="text"
                  className={styles.empInput}
                  placeholder="Search name, ID, or email…"
                  value={empSearch}
                  onChange={e => { setEmpSearch(e.target.value); setShowEmpDropdown(true); }}
                  onFocus={() => setShowEmpDropdown(true)}
                />
                {empSearch && (
                  <button
                    className={styles.empClearBtn}
                    onMouseDown={() => { setEmpSearch(''); setSelectedEmpId(''); setShowEmpDropdown(false); }}
                    tabIndex={-1}
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
              {showEmpDropdown && filteredEmployees.length > 0 && (
                <ul className={styles.empDropdown}>
                  {filteredEmployees.slice(0, 10).map(emp => (
                    <li
                      key={emp.id}
                      className={`${styles.empDropdownItem} ${selectedEmpId === emp.id ? styles.empDropdownItemActive : ''}`}
                      onMouseDown={() => handleSelectEmployee(emp)}
                    >
                      <img
                        src={emp.photoURL || '/default-avatar.png'}
                        alt={emp.name || 'Avatar'}
                        className={styles.empDropdownAvatar}
                      />
                      <div className={styles.empDropdownInfo}>
                        <strong>{emp.name || 'Unnamed'}</strong>
                        <span>
                          {emp.designation || emp.role || '—'}
                          {emp.employeeId ? ` · ${emp.employeeId}` : ''}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Photo upload */}
          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Employee Photo</h3>
            <div
              className={styles.photoUploadArea}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && fileInputRef.current?.click()}
            >
              {photo ? (
                <>
                  <img src={photo} alt="Employee" className={styles.photoPreview} />
                  <div className={styles.photoOverlayHint}>
                    <FaCamera /> Change
                  </div>
                </>
              ) : (
                <div className={styles.photoPlaceholder}>
                  <FaUser size={28} />
                  <span>Click to upload</span>
                  <small>JPG / PNG / WEBP</small>
                </div>
              )}
            </div>
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              onChange={handlePhotoUpload}
              style={{ display: 'none' }}
            />
          </div>

          {/* Card detail fields */}
          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Card Details (Front)</h3>
            <div className={styles.fieldGrid}>

              <div className={styles.fieldRow}>
                <label className={styles.fieldLabel}>Full Name</label>
                <input
                  type="text"
                  className={styles.fieldInput}
                  placeholder="Nipun Dayal"
                  value={cardData.name}
                  onChange={e => handleChange('name', e.target.value)}
                />
              </div>

              <div className={styles.fieldRow}>
                <label className={styles.fieldLabel}>Employee ID</label>
                <input
                  type="text"
                  className={styles.fieldInput}
                  placeholder="EMP20250320"
                  value={cardData.employeeId}
                  onChange={e => handleChange('employeeId', e.target.value)}
                />
              </div>

              <div className={styles.fieldRow}>
                <label className={styles.fieldLabel}>Joining Date</label>
                <input
                  type="date"
                  className={styles.fieldInput}
                  value={cardData.joiningDate}
                  onChange={e => handleChange('joiningDate', e.target.value)}
                />
              </div>

            </div>
          </div>

          {/* 5 Principles (Back side) */}
          <div className={styles.formSection}>
            <h3 className={styles.sectionTitle}>5 Principles of Success (Back)</h3>
            <div className={styles.fieldGrid}>
              {[1, 2, 3, 4, 5].map(n => (
                <div key={n} className={styles.fieldRow}>
                  <label className={styles.fieldLabel}>
                    <span
                      className={styles.principleDot}
                      style={{ background: PRINCIPLE_COLORS[n - 1] }}
                    />
                    Principle {n}
                  </label>
                  <input
                    type="text"
                    className={styles.fieldInput}
                    placeholder={`Enter principle ${n}…`}
                    value={cardData[`principle${n}`]}
                    onChange={e => handleChange(`principle${n}`, e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ════ RIGHT: Preview + Download ════ */}
        <div className={styles.previewPanel}>
          <div className={styles.previewHeader}>
            <h3 className={styles.previewTitle}><FaIdCard /> Live Preview</h3>
            <span className={styles.previewBadge}>4K HD on download</span>
          </div>

          {/* ── FRONT Card ── */}
          <p className={styles.cardSideLabel}>Front Side</p>
          <div className={styles.cardOuter}>
            <div className={styles.card} ref={cardRef}>

              {/* Template background */}
              <img
                src="/IDCardTemplateImage.png"
                alt=""
                className={styles.cardBg}
                crossOrigin="anonymous"
              />

              {/* Photo overlay */}
              <div className={styles.photoSlot}>
                {photo ? (
                  <img
                    src={photo}
                    alt={cardData.name || 'Photo'}
                    className={styles.photoSlotImg}
                    crossOrigin="anonymous"
                    data-download-fix="photo"
                  />
                ) : (
                  <div className={styles.photoSlotEmpty}>
                    <FaUser />
                  </div>
                )}
              </div>

              {/* Text overlays */}
              {cardData.name && (
                <div className={styles.ovName}>{cardData.name}</div>
              )}
              {cardData.employeeId && (
                <div className={styles.ovEmpId}>{cardData.employeeId}</div>
              )}
              {cardData.joiningDate && (
                <div className={styles.ovJoining}>
                  {`Joining Date  :  ${cardData.joiningDate.split('-').reverse().join('/')}`}
                </div>
              )}

            </div>
          </div>

          {/* ── BACK Card ── */}
          <p className={styles.cardSideLabel}>Back Side</p>
          <div className={styles.cardOuter}>
            <div className={styles.card} ref={backCardRef}>

              {/* Back template background */}
              <img
                src="/IDBackSideTemplate.png"
                alt=""
                className={styles.cardBg}
                crossOrigin="anonymous"
              />

              {/* 5 Principles overlays */}
              {[1, 2, 3, 4, 5].map(n => {
                const text = cardData[`principle${n}`];
                if (!text) return null;
                return (
                  <div key={n} className={styles.ovPrinciple} style={{ top: `${92 + (n - 1) * 46}px` }}>
                    <span
                      className={styles.ovBullet}
                      style={{ background: PRINCIPLE_COLORS[n - 1] }}
                    />
                    <span
                      className={styles.ovPrincipleText}
                      style={{ color: PRINCIPLE_COLORS[n - 1] }}
                    >
                      {text}
                    </span>
                  </div>
                );
              })}

            </div>
          </div>

          {/* Download buttons */}
          <div className={styles.downloadRow}>
            <button
              className={styles.downloadBtn}
              onClick={() => downloadCard(cardRef, 'Front', setIsDownloadingFront)}
              disabled={isDownloadingFront}
            >
              {isDownloadingFront ? (
                <><span className={styles.btnSpinner} /> Generating…</>
              ) : (
                <><FaDownload /> Download Front</>
              )}
            </button>
            <button
              className={`${styles.downloadBtn} ${styles.downloadBtnBack}`}
              onClick={() => downloadCard(backCardRef, 'Back', setIsDownloadingBack)}
              disabled={isDownloadingBack}
            >
              {isDownloadingBack ? (
                <><span className={styles.btnSpinner} /> Generating…</>
              ) : (
                <><FaDownload /> Download Back</>
              )}
            </button>
          </div>

          {/* Soft Copy PDF button */}
          <button
            className={`${styles.downloadBtn} ${styles.downloadBtnPdf}`}
            onClick={downloadSoftCopyPdf}
            disabled={isDownloadingPdf || isDownloadingFront || isDownloadingBack}
          >
            {isDownloadingPdf ? (
              <><span className={styles.btnSpinner} /> Building PDF…</>
            ) : (
              <><FaFilePdf /> Download Soft Copy (PDF)</>
            )}
          </button>

          <p className={styles.downloadNote}>
            PNG buttons save 4K print-ready individual sides. PDF button generates a professional single-page soft copy with both sides.
          </p>

          {/* Overlay position notice */}
          <div className={styles.positionNote}>
            <strong>Note:</strong> Text overlay positions are pre-set to match the
            uploaded template. If any field appears misaligned, let your developer
            adjust the CSS positions in <code>IDCardGenerator.module.css</code>.
          </div>
        </div>

      </div>
    </div>
  );
};

export default IDCardGenerator;
